import { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { notify, TransactionNotificationType } from 'utils/notifications'
import Loading from '@components/loading/Loading'
import { Transition } from '@headlessui/react'
import { CLIENT_TX_TIMEOUT } from 'utils/constants'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { useTranslation } from 'next-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import store from 'stores/store'
import { useWalletTokens } from 'hooks/useWalletTokens'
import {
  NOTIFICATION_POSITION_KEY,
  NotificationPosition,
} from '@components/settings/DisplaySettings'
import { usePreferredExplorerUrl } from 'hooks/usePreferredExplorerUrl'
import { ExternalLink } from '@components/shared/ExternalLink'
import { clsx } from 'clsx'

const set = store.getState().set

function parseDescription(
  description: string | null | undefined,
  fallback: string,
) {
  if (
    description?.includes('{"err":{"InstructionError":[2,{"Custom":6001}]}}')
  ) {
    return fallback
  }

  return description
}

const TransactionNotificationList = () => {
  const { t } = useTranslation('transaction-notifications')
  const transactionNotifications = store((s) => s.transactionNotifications)
  const { walletTokens } = useWalletTokens()
  const notEnoughSOLMessage = t('deposit-more-sol')
  const [notificationPosition] = useLocalStorage(NOTIFICATION_POSITION_KEY)
  const [mounted, setMounted] = useState(false)

  // if a notification is shown with {"InstructionError":[0,{"Custom":1}]} then
  // add a notification letting the user know they may not have enough SOL
  useEffect(() => {
    if (transactionNotifications.length) {
      const customErrorNotification = transactionNotifications.find(
        (n) => n.description && n.description.includes('"Custom":1'),
      )
      const notEnoughSolNotification = transactionNotifications.find(
        (n) => n.title && n.title.includes(notEnoughSOLMessage),
      )

      if (!notEnoughSolNotification && customErrorNotification) {
        notify({
          title: notEnoughSOLMessage,
          type: 'info',
        })
      }
    }
  }, [notEnoughSOLMessage, transactionNotifications, walletTokens])

  const clearAll = useCallback(() => {
    set((s) => {
      s.transactionNotifications = s.transactionNotifications.map((n) => ({
        ...n,
        show: false,
      }))
    })
  }, [])

  const reversedNotifications = [...transactionNotifications].reverse()

  const getPosition = (position: NotificationPosition) => {
    const sharedClasses =
      'pointer-events-none fixed z-50 flex items-end p-4 text-th-fgd-1 md:p-6'
    switch (position) {
      case 'Bottom-Left':
        return 'flex-col bottom-0 left-0 ' + sharedClasses
      case 'Bottom-Right':
        return 'flex-col w-full bottom-0 right-0 ' + sharedClasses
      case 'Top-Left':
        return 'flex-col-reverse top-0 left-0 ' + sharedClasses
      case 'Top-Right':
        return 'flex-col-reverse w-full top-0 right-0 ' + sharedClasses
      default:
        return 'flex-col bottom-0 left-0 ' + sharedClasses
    }
  }

  useEffect(() => setMounted(true), [])
  if (!mounted) return null

  return (
    <div
      className={clsx(getPosition(notificationPosition), 'w-full sm:w-auto')}
    >
      {transactionNotifications.filter((n) => n.show).length > 1 ? (
        <button
          className="pointer-events-auto my-1 flex items-center rounded bg-th-bkg-3 px-2 py-1 text-xs text-th-fgd-3 md:hover:bg-th-bkg-4"
          onClick={clearAll}
        >
          <XMarkIcon className="mr-1 size-3.5" />
          <span>{t('clear-all')}</span>
        </button>
      ) : null}
      {reversedNotifications.map((n) => (
        <TransactionNotification key={n.id} notification={n} />
      ))}
    </div>
  )
}

const TransactionNotification = ({
  notification,
}: {
  notification: TransactionNotificationType
}) => {
  const { t } = useTranslation('transaction-notifications')
  const [notificationPosition] = useLocalStorage(NOTIFICATION_POSITION_KEY)
  const makeExplorerUrlForTransaction = usePreferredExplorerUrl('transaction')
  const { type, title, description, txid, show, id } = notification

  // overwrite the title if of the error message if it is a timeout error
  let parsedTitle: string | undefined
  if (description) {
    if (
      description.includes('Timed out awaiting') ||
      description.includes('was not confirmed')
    ) {
      parsedTitle = t('transaction-status-unknown')
    }
  }

  const parsedDescription = parseDescription(
    description,
    t('max-slippage-tolerance-exceeded'),
  )

  // if the notification is a success, then hide the confirming tx notification with the same txid
  useEffect(() => {
    if ((type === 'error' || type === 'success') && txid) {
      set((s) => {
        const newNotifications = s.transactionNotifications.map((n) =>
          n.txid === txid && n.type === 'confirm' ? { ...n, show: false } : n,
        )
        s.transactionNotifications = newNotifications
      })
    }
  }, [type, txid])

  const hideNotification = useCallback(() => {
    set((s) => {
      const newNotifications = s.transactionNotifications.map((n) =>
        n.id === id ? { ...n, show: false } : n,
      )
      s.transactionNotifications = newNotifications
    })
  }, [id])

  // auto hide a notification
  // if no status is provided for a tx notification after 90s, hide it
  useEffect(() => {
    const timeoutInterval =
      parsedTitle || type === 'confirm'
        ? CLIENT_TX_TIMEOUT
        : type === 'error'
          ? 30000
          : type === 'info'
            ? 8000
            : 10000

    const id = setTimeout(() => {
      if (show) {
        hideNotification()
      }
    }, timeoutInterval)

    return () => {
      clearInterval(id)
    }
  }, [hideNotification, parsedTitle, show, type])

  const getTransformClasses = (position: NotificationPosition) => {
    const fromLeft = {
      enterFromClass: 'md:-translate-x-48',
      enterToClass: 'md:translate-x-100',
      leaveFromClass: 'md:translate-x-0',
      leaveToClass: 'md:-translate-x-48',
    }
    const fromRight = {
      enterFromClass: 'md:translate-x-48',
      enterToClass: 'md:-translate-x-100',
      leaveFromClass: 'md:translate-x-0',
      leaveToClass: 'md:translate-x-48',
    }
    switch (position) {
      case 'Bottom-Left':
        return fromLeft
      case 'Bottom-Right':
        return fromRight
      case 'Top-Left':
        return fromLeft
      case 'Top-Right':
        return fromRight
      default:
        return fromLeft
    }
  }

  return (
    <Transition
      show={show}
      appear={true}
      enter="ease-out duration-500 transition"
      enterFrom={`-translate-y-2 opacity-0 md:translate-y-0 ${
        getTransformClasses(notificationPosition).enterFromClass
      }`}
      enterTo={`translate-y-0 opacity-100 ${
        getTransformClasses(notificationPosition).enterToClass
      }`}
      leave="ease-in duration-200 transition"
      leaveFrom={`translate-y-0 ${
        getTransformClasses(notificationPosition).leaveFromClass
      }`}
      leaveTo={`-translate-y-2 md:translate-y-0 ${
        getTransformClasses(notificationPosition).leaveToClass
      }`}
    >
      <div
        className={`pointer-events-auto my-1 w-full rounded-md bg-th-bkg-2 shadow-lg sm:w-auto ${
          type === 'success'
            ? 'border border-th-success'
            : type === 'error'
              ? 'border border-th-error'
              : ''
        }`}
      >
        <div className="relative flex w-full items-center p-4 sm:w-96">
          <div className="mr-1 shrink-0">
            {type === 'success' ? (
              <CheckCircleIcon className="size-6 text-th-success" />
            ) : null}
            {type === 'info' && (
              <InformationCircleIcon className="size-6 text-th-fgd-3" />
            )}
            {type === 'error' && (
              <XCircleIcon className="size-6 text-th-error" />
            )}
            {type === 'confirm' && (
              <Loading className="mr-0.5 size-5 text-th-active" />
            )}
          </div>
          <div className="ml-2 flex-1">
            <p className="break-all text-th-fgd-1">{parsedTitle || title}</p>
            {parsedDescription ? (
              <div className="mb-0 mt-0.5 text-sm leading-tight text-th-fgd-4">
                <ReactMarkdown
                  components={{ a: LinkRenderer }}
                  className="markdown"
                  remarkPlugins={[remarkGfm]}
                >
                  {parsedDescription}
                </ReactMarkdown>
              </div>
            ) : null}
            {txid ? (
              <ExternalLink
                href={makeExplorerUrlForTransaction(txid)}
                className="mt-1 flex items-center text-xs text-th-active underline hover:text-th-fgd-2"
              >
                <div className="break-all">
                  {type === 'error'
                    ? txid
                    : `${txid.slice(0, 14)}...${txid.slice(txid.length - 14)}`}
                </div>
                <ArrowTopRightOnSquareIcon className="mb-0.5 ml-1 size-5 shrink-0" />
              </ExternalLink>
            ) : null}
          </div>
          <div className="absolute right-2 top-2 shrink-0">
            <button
              onClick={hideNotification}
              className="text-th-fgd-4 focus:outline-none md:hover:text-th-fgd-3"
            >
              <span className="sr-only">{t('close')}</span>
              <svg
                className="size-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  )
}

export default TransactionNotificationList

function LinkRenderer({
  href,
  children,
}: {
  href?: string
  children?: ReactNode
}) {
  return <ExternalLink href={href || ''}>{children}</ExternalLink>
}
